'use client';

import { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/entities/Sprite';
import { cn } from '@/utils/cn';
import '../styles/shuffle-coins.css';

type Currency = 'GC' | 'SC';

const COIN_ICON: Record<Currency, 'gold-coin' | 'sweep-coin'> = {
  GC: 'gold-coin',
  SC: 'sweep-coin',
};

const OTHER_CURRENCY: Record<Currency, Currency> = {
  GC: 'SC',
  SC: 'GC',
};

interface Props {
  currency: Currency;
  className?: string;
  onClick?: (isShuffling?: boolean) => void;
}

export default function AnimatedCurrencyCoin({ currency, className, onClick }: Props) {
  const previousCurrencyRef = useRef(currency);
  const [isShuffling, setIsShuffling] = useState(false);

  useEffect(() => {
    if (previousCurrencyRef.current === currency) {
      return;
    }

    setIsShuffling(true);
  }, [currency]);

  const completeShuffle = () => {
    previousCurrencyRef.current = currency;
    setIsShuffling(false);
  };

  const backCurrency = OTHER_CURRENCY[currency];
  const outgoingCurrency = previousCurrencyRef.current;

  return (
    <div
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={cn('relative h-8 w-10 shrink-0 overflow-visible', onClick && 'cursor-pointer', className)}
      onClick={() => onClick?.(isShuffling)}
      onKeyDown={onClick ? e => (e.key === 'Enter' || e.key === ' ') && onClick?.(isShuffling) : undefined}
    >
      <div className="pointer-events-none absolute top-0 left-0 h-8 w-16 overflow-visible">
        <div
          className={cn(
            'absolute inset-0',
            isShuffling ? 'animate-currency-coin-shuffle-to-front' : 'currency-coin-stack-back',
          )}
        >
          <Icon
            name={COIN_ICON[isShuffling ? currency : backCurrency]}
            className="size-8"
          />
        </div>
        <div
          className={cn(
            'absolute inset-0',
            isShuffling ? 'animate-currency-coin-shuffle-to-back' : 'currency-coin-stack-front',
          )}
          onAnimationEnd={isShuffling ? completeShuffle : undefined}
        >
          <Icon
            name={COIN_ICON[isShuffling ? outgoingCurrency : currency]}
            className="size-8"
          />
        </div>
      </div>
    </div>
  );
}
